import type { Category, ContentsItem } from '../types'

export function ObservedContentsEditor({
  items,
  categories,
  international,
  onChange,
}: {
  items: ContentsItem[]
  categories: Category[]
  international: boolean
  onChange: (items: ContentsItem[]) => void
}) {
  const update = (index: number, patch: Partial<ContentsItem>) =>
    onChange(
      items.map((item, n) => (n === index ? { ...item, ...patch } : item)),
    )
  return (
    <section
      className="observed-contents"
      aria-label="Observed package contents"
    >
      <h3>Contents in this package</h3>
      <p className="field-help">
        Record each product type separately. Measurements can change without
        customer approval.
        {international
          ? ' Changes to contents, quantity, value, or origin need the customer’s confirmation.'
          : ''}
      </p>
      {items.map((item, index) => (
        <div className="observed-item" key={item.categoryCode}>
          <div className="observed-item__head">
            <h3>{item.name}</h3>
            <button
              type="button"
              className="button button--ghost"
              aria-label={`Remove ${item.name} line`}
              onClick={() => onChange(items.filter((_, n) => n !== index))}
            >
              Remove
            </button>
          </div>
          <div className="form-grid">
            <label>
              Quantity <small>bottles / packages</small>
              <input
                required
                type="number"
                min="1"
                step="1"
                aria-label={`${item.name} quantity`}
                value={item.quantity || ''}
                onChange={(e) =>
                  update(index, { quantity: Number(e.target.value) })
                }
              />
            </label>
            <label>
              Total contents weight <small>grams, all units in this line</small>
              <input
                required
                type="number"
                min="1"
                step="1"
                aria-label={`${item.name} contents weight`}
                value={item.itemWeightGrams || ''}
                onChange={(e) =>
                  update(index, { itemWeightGrams: Number(e.target.value) })
                }
              />
            </label>
          </div>
          {international && (
            <>
              <label>
                Description
                <input
                  required
                  aria-label={`${item.name} description`}
                  value={item.description}
                  onChange={(e) =>
                    update(index, { description: e.target.value })
                  }
                />
              </label>
              <div className="form-grid">
                <label>
                  Value per unit <small>{item.currency}</small>
                  <input
                    required
                    type="number"
                    min="0.01"
                    step="0.01"
                    aria-label={`${item.name} value per unit`}
                    defaultValue={(item.unitValueMinor / 100).toFixed(2)}
                    onChange={(e) =>
                      update(index, {
                        unitValueMinor: Math.round(
                          Number(e.target.value) * 100,
                        ),
                      })
                    }
                  />
                </label>
                <label>
                  Currency
                  <input
                    required
                    maxLength={3}
                    aria-label={`${item.name} currency`}
                    value={item.currency}
                    onChange={(e) =>
                      update(index, { currency: e.target.value.toUpperCase() })
                    }
                  />
                </label>
                <label>
                  Country of origin
                  <input
                    required
                    minLength={2}
                    maxLength={2}
                    aria-label={`${item.name} origin`}
                    value={item.originCountryCode}
                    onChange={(e) =>
                      update(index, {
                        originCountryCode: e.target.value.toUpperCase(),
                      })
                    }
                  />
                </label>
                <label>
                  HS code
                  <input
                    aria-label={`${item.name} HS code`}
                    value={item.hsCode}
                    onChange={(e) => update(index, { hsCode: e.target.value })}
                  />
                </label>
                <label>
                  Product SKU
                  <input
                    maxLength={100}
                    aria-label={`${item.name} product SKU`}
                    value={item.skuCode ?? ''}
                    onChange={(e) => update(index, { skuCode: e.target.value })}
                  />
                </label>
                <label>
                  Manufacturer product code
                  <input
                    maxLength={100}
                    aria-label={`${item.name} manufacturer product code`}
                    value={item.manufacturerCode ?? ''}
                    onChange={(e) => update(index, { manufacturerCode: e.target.value })}
                  />
                </label>
              </div>
              <label className="customs-consent">
                <input
                  type="checkbox"
                  checked={item.euExemptCategory ?? false}
                  onChange={(e) => update(index, { euExemptCategory: e.target.checked })}
                />
                <span><b>This item qualifies for an EU product-identifier exemption.</b><small>Examples include genuine collectibles, artwork, and antiques. Ordinary craft supplies are not automatically collectibles. A qualifying item does not need a manufacturer product code.</small></span>
              </label>
            </>
          )}
        </div>
      ))}
      <div className="form-actions">
        {categories
          .filter((c) => !items.some((i) => i.categoryCode === c.code))
          .map((c) => (
            <button
              type="button"
              className="button button--outline"
              key={c.code}
              onClick={() =>
                onChange([
                  ...items,
                  {
                    categoryCode: c.code,
                    name: c.displayName,
                    description: c.customsDescription,
                    quantity: 1,
                    itemWeightGrams: c.customsItemWeightGrams,
                    unitValueMinor: c.unitValueMinor,
                    currency: c.currency,
                    hsCode: c.hsCode,
                    originCountryCode: c.originCountryCode,
                    skuCode: c.skuCode,
                    euExemptCategory: c.euExemptCategory,
                  },
                ])
              }
            >
              Add {c.displayName}
            </button>
          ))}
      </div>
      {!items.length && (
        <p className="form-error">
          Add at least one contents line before saving.
        </p>
      )}
    </section>
  )
}
